import React, { useEffect, useState, useRef } from 'react';
import { Users, GraduationCap, BookOpen, Coins } from 'lucide-react';
import { motion, useAnimation, useInView } from 'framer-motion';
export function StatsBanner() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const stats = [{
    icon: <Users className="w-6 h-6" data-id="element-422" />,
    value: 10,
    suffix: 'K+',
    label: 'Active Students Enrolled'
  }, {
    icon: <GraduationCap className="w-6 h-6" data-id="element-423" />,
    value: 105,
    suffix: '+',
    label: 'PhD-Qualified Senior Faculty'
  }, {
    icon: <BookOpen className="w-6 h-6" data-id="element-424" />,
    value: 29,
    suffix: '+',
    label: 'Globally Competitive Programs'
  }, {
    icon: <Coins className="w-6 h-6" data-id="element-425" />,
    value: 11,
    suffix: 'Cr+',
    label: 'BDT Annual Scholarships Distributed'
  }];
  return <section className="relative z-20 -mt-10 max-w-7xl mx-auto px-6" ref={ref} data-id="element-426">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-xl" data-id="element-427">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-200" data-id="element-428">
          {stats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className={`flex flex-col items-center text-center px-4 ${index !== 0 ? 'pt-8 lg:pt-0' : ''}`} data-id="element-429">
              <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center text-navy mb-4" data-id="element-430">
                {stat.icon}
              </div>
              <div className="flex items-baseline space-x-1 mb-2" data-id="element-431">
                <Counter from={0} to={stat.value} duration={2} isInView={isInView} data-id="element-432" />
                <span className="text-3xl font-bold text-navy" data-id="element-433">
                  {stat.suffix}
                </span>
              </div>
              <p className="text-sm text-slate-500 font-medium max-w-[180px] leading-snug" data-id="element-434">
                {stat.label}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function Counter({
  from,
  to,
  duration,
  isInView
}: {
  from: number;
  to: number;
  duration: number;
  isInView: boolean;
}) {
  const [count, setCount] = useState(from);
  useEffect(() => {
    if (!isInView) return;
    let startTime: number;
    let animationFrame: number;
    const updateCount = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      // Ease out expo
      const easeOut = 1 - Math.pow(2, -10 * (progress / (duration * 1000)));
      if (progress < duration * 1000) {
        setCount(Math.floor(from + (to - from) * easeOut));
        animationFrame = requestAnimationFrame(updateCount);
      } else {
        setCount(to);
      }
    };
    animationFrame = requestAnimationFrame(updateCount);
    return () => cancelAnimationFrame(animationFrame);
  }, [from, to, duration, isInView]);
  return <span className="text-4xl lg:text-5xl font-extrabold tracking-tight text-navy" data-id="element-435">
      {count}
    </span>;
}